import React from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage, FormattedNumber} from 'react-intl'
import {
    Box,
    Flex,
    Text,
    Image,
    Stack,
    Divider
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useBrand} from '../../../contexts/brand-context'

export const OrderDetailsSummary = ({
    orderSubtotal = 0,
    merchandizeTotalTax = 0,
    totalTax = 0,
    totalOrderDiscount = 0,
    shippingFee = 0,
    orderTotal = 0,
    currency,
    giftCardTotal = 0
}) => {
    // Ensure all numeric values are valid numbers to prevent NaN
    const safeOrderSubtotal = Number(orderSubtotal) || 0
    const safeMerchandizeTotalTax = Number(merchandizeTotalTax) || 0
    const safeTotalTax = Number(totalTax) || 0
    const safeTotalOrderDiscount = Number(totalOrderDiscount) || 0
    const safeShippingFee = Number(shippingFee) || 0
    const safeOrderTotal = Number(orderTotal) || 0
    const safeGiftCardTotal = Number(giftCardTotal) || 0
    const {isGarage} = useBrand()

    return (
        <Stack spacing={0} lineHeight="18px">
            {/* SUBTOTAL */}
            <Flex w="full" justifyContent="space-between" fontSize="sm">
                <Text>
                    <FormattedMessage
                        id="account_order_detail.label.order_subtotal"
                        defaultMessage="Subtotal (excl. VAT)"
                    />
                </Text>
                <Text>
                    <FormattedNumber
                        style="currency"
                        currency={currency}
                        value={safeOrderSubtotal - safeMerchandizeTotalTax}
                    />
                </Text>
            </Flex>

            {/* TOTAL WITH TAXES */}
            <Flex w="full" justifyContent="space-between" fontSize="sm">
                <Text>
                    <FormattedMessage
                        id="account_order_detail.label.order_subtotal_with_tax"
                        defaultMessage="Subtotal (incl. VAT)"
                    />
                </Text>
                <Text>
                    <FormattedNumber
                        style="currency"
                        currency={currency}
                        value={safeOrderSubtotal}
                    />
                </Text>
            </Flex>

            {safeTotalOrderDiscount > 0 && (
                <Flex w="full" justifyContent="space-between" fontSize="sm">
                    <Text>
                        <FormattedMessage
                            id="account_order_detail.label.order_savings"
                            defaultMessage="Savings"
                        />
                    </Text>
                    <Text>
                        <FormattedNumber
                            style="currency"
                            currency={currency}
                            value={-safeTotalOrderDiscount}
                        />
                    </Text>
                </Flex>
            )}

            {safeGiftCardTotal > 0 && (
                <Flex w="full" justifyContent="space-between" fontSize="sm">
                    <Text>
                        <FormattedMessage
                            defaultMessage="Gift Card"
                            id="order_summary.label.gift_card"
                        />
                    </Text>
                    <Text>
                        <FormattedNumber
                            style="currency"
                            currency={currency}
                            value={-safeGiftCardTotal}
                        />
                    </Text>
                </Flex>
            )}

            {/* SHIPPING */}
            <Flex w="full" justifyContent="space-between" fontSize="sm">
                <Text>
                    <FormattedMessage
                        id="account_order_detail.label.order_shipping_fee"
                        defaultMessage="Shipping"
                    />
                </Text>
                <Text>
                    {safeShippingFee > 0 ? (
                        <FormattedNumber
                            style="currency"
                            currency={currency}
                            value={safeShippingFee}
                        />
                    ) : (
                        <FormattedMessage
                            id="checkout_confirmation.label.free"
                            defaultMessage="Free"
                        />
                    )}
                </Text>
            </Flex>

            {/* GRAND TOTAL */}
            <Flex
                w="full"
                justifyContent="space-between"
                fontSize="sm"
                textTransform="uppercase"
                fontWeight="bold"
                lineHeight="16px"
                mt={3}
            >
                <Text>
                    {isGarage ? (
                        <FormattedMessage
                            id="account_order_detail.label.order_grand_total.garage"
                            defaultMessage="Your Total"
                        />
                    ) : (
                        <FormattedMessage
                            id="account_order_detail.label.order_grand_total.dynamite"
                            defaultMessage="Your Total"
                        />
                    )}
                </Text>
                <Text>
                    <FormattedNumber
                        style="currency"
                        currency={currency}
                        value={safeOrderTotal - safeGiftCardTotal}
                    />
                </Text>
            </Flex>

            {/* VAT */}
            <Flex w="full" justifyContent="space-between" fontSize="sm">
                <Text>
                    <FormattedMessage
                        id="account_order_detail.label.order_tax"
                        defaultMessage="VAT (20%)"
                    />
                </Text>
                <Text>
                    <FormattedNumber style="currency" currency={currency} value={safeTotalTax} />
                </Text>
            </Flex>
        </Stack>
    )
}

OrderDetailsSummary.propTypes = {
    orderSubtotal: PropTypes.number,
    merchandizeTotalTax: PropTypes.number,
    totalTax: PropTypes.number,
    totalOrderDiscount: PropTypes.number,
    shippingFee: PropTypes.number,
    orderTotal: PropTypes.number,
    currency: PropTypes.string.isRequired,
    giftCardTotal: PropTypes.number
}
